import React, { useEffect, useState } from "react";
import { FaCrown, FaTrophy, FaRocket, FaArrowLeft } from "react-icons/fa";
import { FiCheckCircle, FiStar, FiZap, FiTarget } from "react-icons/fi";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import * as api from "../../api/index";

// Seller Levels Data (Icons updated for modern look)
const sellerLevels = [
  {
    id: 1,
    title: "Starter Seller",
    subtitle: "Level 1",
    description: "Welcome! Start fulfilling orders and earn your first reputation points.",
    tasks: ["Complete 5 deliveries", "Serve 5 buyers"],
    perks: ["Basic dashboard access", "Seller community access"],
    icon: <FiZap />,
    color: "#3b82f6", // Blue
    bg: "bg-blue-50",
  },
  {
    id: 2,
    title: "Growing Seller",
    subtitle: "Level 2",
    description: "Your reputation is growing! Unlock analytics tools and faster support.",
    tasks: ["Complete 15 deliveries", "Serve 10 buyers"],
    perks: ["Priority notifications", "Discounted promotions", "Performance reports"],
    icon: <FiTarget />,
    color: "#10b981", // Green
    bg: "bg-green-50",
  },
  {
    id: 3,
    title: "Pro Seller",
    subtitle: "Level 3",
    description: "You’re popular in your area! Gain exclusive visibility and perks.",
    tasks: ["Complete 30 deliveries", "Serve at least 30 buyers"],
    perks: ["Top sellers list", "VIP support", "Early campaign access"],
    icon: <FaTrophy />,
    color: "#f59e0b", // Yellow/Orange
    bg: "bg-amber-50",
  },
  {
    id: 4,
    title: "Elite Seller",
    subtitle: "Level 4",
    description: "Top-tier seller! Access exclusive campaigns and premium support.",
    tasks: ["Complete 100 deliveries", "Serve 100+ buyers"],
    perks: ["Personal manager", "VIP badge & visibility", "International support"],
    icon: <FaCrown />,
    color: "#8b5cf6", // Purple
    bg: "bg-purple-50",
  },
];

export default function SellerLevels() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLevel = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setUserData({
            level: 2,
            progress: 45,
            totalOrders: 18,
            totalSpent: 65000,
          });
          setLoading(false);
          return;
        }

        const decoded = jwtDecode(token);
        if (!decoded?.id) throw new Error("Invalid token format.");

        const userId = decoded.id;
        const res = await api.updateLevel(userId);

        if (!res?.data) throw new Error("Invalid response from server.");
        setUserData(res.data.data);
      } catch (err) {
        setError("Could not fetch seller level data.");
      } finally {
        setLoading(false);
      }
    };

    fetchLevel();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
        <p className="mt-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">Calculating Progress...</p>
      </div>
    );
  }

  const currentLevel = userData?.level || 1;

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700">
          <FaArrowLeft size={20} />
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Seller Journey</h1>
        <div className="w-10"></div>
      </div>

      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        {/* 🏆 User Status Card */}
        <div className="bg-white p-6 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 mb-10">
            <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-orange-600 rounded-3xl flex items-center justify-center text-white shadow-lg shadow-orange-200">
                    <FiStar size={30} strokeWidth={3} />
                </div>
                <div>
                    <p className="text-[10px] font-black text-orange-600 uppercase tracking-[0.2em]">Current Status</p>
                    <h2 className="text-2xl font-black text-gray-900">Level {currentLevel} Seller</h2>
                </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-2xl">
                    <p className="text-[10px] font-black text-gray-400 uppercase">Deliveries</p>
                    <p className="text-lg font-black text-gray-900">{userData.totalOrders}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-2xl">
                    <p className="text-[10px] font-black text-gray-400 uppercase">Earnings</p>
                    <p className="text-lg font-black text-gray-900">₦{userData.totalSpent.toLocaleString()}</p>
                </div>
            </div>
        </div>

        {error && <div className="text-center text-red-600 font-bold bg-red-50 p-4 rounded-2xl mb-6">{error}</div>}

        {/* 🗺 Journey Steps */}
        <div className="space-y-8 relative">
          {/* Vertical Line Connector */}
          <div className="absolute left-8 top-10 bottom-10 w-0.5 bg-gray-200 -z-0" />

          {sellerLevels.map((level) => {
            const isCurrent = level.id === currentLevel;
            const isLocked = level.id > currentLevel;

            return (
              <div
                key={level.id}
                className={`relative z-10 p-6 rounded-[2rem] border transition-all duration-300 ${
                  isCurrent 
                  ? "bg-white shadow-2xl shadow-gray-200 border-orange-200 scale-[1.02]" 
                  : "bg-white/60 border-gray-100 opacity-80"
                }`}
              >
                {/* Level Indicator Dot */}
                <div className={`absolute -left-10 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-4 border-gray-50 z-20 ${isCurrent ? 'bg-orange-600 scale-150' : isLocked ? 'bg-gray-300' : 'bg-green-500'}`} />

                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-4 rounded-2xl text-2xl ${isCurrent ? 'bg-orange-600 text-white shadow-lg shadow-orange-100' : 'bg-gray-100 text-gray-400'}`}>
                      {level.icon}
                    </div>
                    <div>
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isCurrent ? 'text-orange-600' : 'text-gray-400'}`}>
                        {level.subtitle}
                      </p>
                      <h3 className="text-xl font-black text-gray-900">{level.title}</h3>
                    </div>
                  </div>
                  {isCurrent && (
                    <span className="bg-orange-100 text-orange-700 text-[10px] font-black px-3 py-1 rounded-full uppercase">Active</span>
                  )}
                </div>

                <p className="text-sm text-gray-500 font-medium mb-6 leading-relaxed">
                  {level.description}
                </p>

                <div className="grid sm:grid-cols-2 gap-6">
                  {/* Tasks Section */}
                  <div>
                    <h4 className="text-[10px] font-black text-gray-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                       Requirements
                    </h4>
                    <ul className="space-y-2">
                      {level.tasks.map((task, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-xs font-bold text-gray-600">
                          <FiCheckCircle className={isCurrent ? "text-orange-500" : "text-gray-300"} /> {task}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Perks Section */}
                  <div>
                    <h4 className="text-[10px] font-black text-gray-900 uppercase tracking-widest mb-3">
                      Unlockable Perks
                    </h4>
                    <ul className="space-y-2">
                      {level.perks.map((perk, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-xs font-bold text-gray-400">
                          <div className="w-1 h-1 bg-gray-300 rounded-full" /> {perk}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Progress Bar for Current Level */}
                {isCurrent && (
                  <div className="mt-8 pt-6 border-t border-gray-50">
                    <div className="flex justify-between items-end mb-2">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Next Level Progress</p>
                        <p className="text-xs font-black text-orange-600">{userData.progress}%</p>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-full bg-orange-600 rounded-full transition-all duration-1000 ease-out shadow-[0_0_12px_rgba(234,88,12,0.4)]"
                        style={{ width: `${userData.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}